import { useMemo, useState } from 'react'

import {
  Input,
  Select,
  createListCollection,
  useFilter,
} from '@chakra-ui/react'

import { GitBranchIcon } from '#components/icons/git-branch-icon.tsx'

interface BranchSelectProps {
  branches: Array<{ name: string }>
  value: string[]
  onValueChange: (details: { value: string[] }) => void
  placeholder?: string
  disabled?: boolean
}

export function BranchSelect({
  branches,
  value,
  onValueChange,
  placeholder = 'Select branch',
  disabled = false,
}: BranchSelectProps) {
  const { contains } = useFilter({ sensitivity: 'base' })

  const collection = useMemo(() => {
    return createListCollection({
      items: branches,
      itemToString: (item) => item.name,
      itemToValue: (item) => item.name,
    })
  }, [branches])

  const [searchQuery, setSearchQuery] = useState('')

  const filteredItems = useMemo(() => {
    if (!searchQuery) return collection.items
    return collection.items.filter((item) => contains(item.name, searchQuery))
  }, [collection.items, searchQuery, contains])

  return (
    <Select.Root
      collection={collection}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      size="sm"
      variant="subtle"
      positioning={{ sameWidth: false }}
      maxW="140px"
    >
      <Select.HiddenSelect />
      <Select.Control>
        <Select.Trigger
          justifyContent="flex-start"
          gap="2"
          borderRadius="full"
          bg="transparent"
          _hover={{ bg: 'bg.subtle', borderColor: 'transparent' }}
        >
          <GitBranchIcon />
          <Select.ValueText placeholder={placeholder} />
        </Select.Trigger>
      </Select.Control>
      <Select.Positioner>
        <Select.Content
          flexDirection="column"
          minH="0"
          maxH="calc(var(--available-height) - 4rem)"
          maxW="300px"
        >
          <Select.ItemGroup>
            <Input
              name="branch"
              placeholder="Search branches"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
            />
          </Select.ItemGroup>
          <Select.ItemGroup overflowY="auto" flex="1">
            {filteredItems.map((item) => (
              <Select.Item key={item.name} item={item} lineClamp={1}>
                {item.name}
              </Select.Item>
            ))}
          </Select.ItemGroup>
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  )
}
